/**
 * The onboarding wizard's cloud calls (SC-438). See `cloud/onboardingManager.js`.
 */
import type { OnboardingState, OnboardingStep } from '../../../types/Onboarding'
import { parseCloudRun } from './parseCloud'
import { isMissingCloudFunction } from '../consent/consentService'

export const ONBOARDING_STATE_TIMEOUT_MS = 5000

const NOT_ONBOARDING: OnboardingState = { active: false }

/**
 * Whether to show the wizard, and which step. Fails open on every failure (an older cloud
 * without the function, any error, no answer within the timeout): the wizard is a guide, and
 * must never lock anyone out of the app. Billing still restricts a company without a trial.
 */
export async function loadOnboardingState(
  timeoutMs: number = ONBOARDING_STATE_TIMEOUT_MS
): Promise<OnboardingState> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  try {
    const result = await Promise.race([
      parseCloudRun<OnboardingState>('getOnboardingState', {}),
      timeout,
    ])
    if (result === 'timeout') {
      console.warn(`[Onboarding] No onboarding state after ${timeoutMs} ms; skipping the wizard.`)
      return NOT_ONBOARDING
    }
    return result?.active ? result : NOT_ONBOARDING
  } catch (err) {
    if (isMissingCloudFunction(err)) {
      console.warn('[Onboarding] This cloud has no getOnboardingState yet; skipping the wizard.')
    } else {
      console.warn('[Onboarding] Could not load the onboarding state; skipping the wizard.', err)
    }
    return NOT_ONBOARDING
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * The state again, for a wizard already on screen. Unlike `loadOnboardingState` this throws on a
 * failure: mid-flow, a slow or failed answer must not read as "onboarding is over" and drop the
 * person into the app. A real `{ active: false }` (onboarding finished) still comes back as is.
 */
export async function refreshOnboardingState(): Promise<OnboardingState> {
  const result = await parseCloudRun<OnboardingState>('getOnboardingState', {})
  return result?.active ? result : NOT_ONBOARDING
}

/** Finish `from`, the step on screen. Returns the state after it. */
export function advanceOnboarding(from: OnboardingStep): Promise<OnboardingState> {
  return parseCloudRun<OnboardingState>('advanceOnboarding', { from })
}

/** Step back from `from`. The cloud allows only "Invite your team" → "Your Sevv self". */
export function goBackOnboarding(from: OnboardingStep): Promise<OnboardingState> {
  return parseCloudRun<OnboardingState>('goBackOnboarding', { from })
}

/** The invite CSV row limit, Parse Config `ONBOARDING_INVITE_CSV_MAX_ROWS` (D12); 200 when unset. */
export async function loadInviteCsvMaxRows(fallback = 200): Promise<number> {
  try {
    const { default: Parse } = await import('./initializeParse')
    const value = Number((await Parse.Config.get()).get('ONBOARDING_INVITE_CSV_MAX_ROWS'))
    return Number.isFinite(value) && value > 0 ? value : fallback
  } catch {
    return fallback
  }
}
